import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { canWrite, canDelete, WRITE_PERMISSION_DENIED, DELETE_PERMISSION_DENIED } from '@/services/permissions';
import { recordAudit } from '@/services/audit';

export type PurchaseStatus = 'pending' | 'partial' | 'paid' | 'cancelled';

export const PURCHASE_STATUS_LABELS: Record<PurchaseStatus, string> = {
  pending: 'Pendiente',
  partial: 'Abonada',
  paid: 'Pagada',
  cancelled: 'Cancelada',
};

export interface Purchase {
  id: string;
  invoiceNumber: string;
  supplierId: string;
  supplierName: string;
  totalCost: number;
  itemCount: number;
  notes: string;
  createdAt: string;
  status: PurchaseStatus;
  paidAmount: number;
  balance: number;
  receiptUrl: string;
}

// Calcula el estado de pago de una compra a partir de lo abonado. El estado
// 'cancelled' siempre manda (se anula manualmente).
export function computePurchaseStatus(
  total: number,
  paid: number,
  stored: string,
): PurchaseStatus {
  if (stored === 'cancelled') return 'cancelled';
  if (paid <= 0) return 'pending';
  if (paid + 0.01 < total) return 'partial';
  return 'paid';
}

export interface PurchasesResult {
  data: Purchase[];
}

// Consulta las compras reales desnormalizando proveedor e items. Sin datos
// devuelve un array vacío (honesto) en vez de inventar mocks.
export async function fetchPurchases(): Promise<PurchasesResult> {
  if (!isSupabaseConfigured || !supabase) {
    return { data: [] };
  }

  try {
    const [purchasesRes, suppliersRes, itemsRes, paymentsRes] = await Promise.all([
      supabase.from('purchases').select('*'),
      supabase.from('suppliers').select('id, company_name'),
      supabase.from('purchase_items').select('purchase_id, quantity'),
      supabase.from('supplier_payments').select('purchase_id, amount'),
    ]);

    if (purchasesRes.error) throw new Error(purchasesRes.error.message);
    if (suppliersRes.error) throw new Error(suppliersRes.error.message);
    if (itemsRes.error) throw new Error(itemsRes.error.message);

    const supplierMap = new Map(
      ((suppliersRes.data ?? []) as Array<Record<string, any>>).map((s) => [s.id, s.company_name]),
    );

    const countByPurchase = new Map<string, number>();
    ((itemsRes.data ?? []) as Array<Record<string, any>>).forEach((it) => {
      countByPurchase.set(
        it.purchase_id,
        (countByPurchase.get(it.purchase_id) ?? 0) + Number(it.quantity),
      );
    });

    const paidByPurchase = new Map<string, number>();
    ((paymentsRes.data ?? []) as Array<Record<string, any>>).forEach((p) => {
      if (!p.purchase_id) return;
      paidByPurchase.set(p.purchase_id, (paidByPurchase.get(p.purchase_id) ?? 0) + Number(p.amount));
    });

    const data: Purchase[] = ((purchasesRes.data ?? []) as Array<Record<string, any>>)
      .map((p) => {
        const totalCost = Number(p.total_cost);
        const paidAmount = paidByPurchase.get(p.id) ?? 0;
        return {
          id: p.id,
          invoiceNumber: p.invoice_number ?? '',
          supplierId: p.supplier_id ?? '',
          supplierName: p.supplier_id ? supplierMap.get(p.supplier_id) ?? '' : '',
          totalCost,
          itemCount: countByPurchase.get(p.id) ?? 0,
          notes: p.notes ?? '',
          createdAt: p.created_at ?? '',
          status: computePurchaseStatus(totalCost, paidAmount, (p.status ?? 'pending') as string),
          paidAmount,
          balance: Math.max(0, totalCost - paidAmount),
          receiptUrl: p.receipt_url ?? '',
        };
      })
      .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));

    return { data };
  } catch (err) {
    console.error('[purchases] connection failed:', err);
    throw err;
  }
}

// Item de la compra (referencia + talla + cantidad + precios).
export interface PurchaseItemInput {
  variantId: string;
  productId: string;
  quantity: number;
  unitCost: number;
  retailPrice: number;
}

// Entrada del formulario de compra (crear).
export interface PurchaseInput {
  supplierId: string;
  invoiceNumber: string;
  notes: string;
  items: PurchaseItemInput[];
}

// Crea una compra (factura) con sus items en Supabase.
export async function savePurchase(
  input: PurchaseInput,
): Promise<{ data: Purchase | null; error: string | null }> {
  if (!(await canWrite('purchases'))) {
    return { data: null, error: WRITE_PERMISSION_DENIED };
  }
  if (!isSupabaseConfigured || !supabase) {
    return { data: null, error: 'Supabase no está configurado.' };
  }

  const totalCost = input.items.reduce((sum, it) => sum + it.quantity * it.unitCost, 0);

  try {
    const { data, error } = await supabase
      .from('purchases')
      .insert({
        supplier_id: input.supplierId || null,
        invoice_number: input.invoiceNumber.trim() || null,
        total_cost: totalCost,
        notes: input.notes.trim() || null,
      })
      .select('*');

    if (error) return { data: null, error: error.message };
    const purchase = data?.[0] as Record<string, any> | undefined;
    if (!purchase) return { data: null, error: 'No se pudo crear la compra.' };

    recordAudit({
      entityType: 'purchases',
      entityId: purchase.id,
      action: 'CREATE',
      newValue: input.invoiceNumber.trim() || 'Compra',
    });

    if (input.items.length > 0) {
      const items = input.items.map((it) => ({
        purchase_id: purchase.id,
        variant_id: it.variantId,
        quantity: it.quantity,
        unit_cost: it.unitCost,
        retail_price: it.retailPrice,
      }));
      const { error: itemsError } = await supabase.from('purchase_items').insert(items);
      if (itemsError) console.warn('[purchases] items insert failed:', itemsError.message);

      // Actualiza el precio de compra y venta de cada referencia (producto)
      // comprada. El precio queda ligado a la referencia, no a la talla, y se
      // refleja solo en tienda, inventario y reportes.
      const productPrices = new Map<string, { unitCost: number; retailPrice: number }>();
      for (const it of input.items) {
        if (it.productId) {
          productPrices.set(it.productId, { unitCost: it.unitCost, retailPrice: it.retailPrice });
        }
      }
      for (const [productId, prices] of productPrices) {
        const { error: priceErr } = await supabase
          .from('products')
          .update({ unit_cost: prices.unitCost, retail_price: prices.retailPrice })
          .eq('id', productId);
        if (priceErr) console.warn('[purchases] product price update failed:', priceErr.message);
      }

      // Suma al inventario cada insumo comprado y registra el movimiento.
      for (const it of input.items) {
        const { data: inv } = await supabase
          .from('inventory')
          .select('available, reserved, consigned')
          .eq('variant_id', it.variantId)
          .maybeSingle();
        const prev = Number(inv?.available ?? 0);
        const reserved = Number(inv?.reserved ?? 0);
        const consigned = Number(inv?.consigned ?? 0);
        const next = prev + it.quantity;

        // Upsert: si la variante aún no tiene fila de inventario (p. ej. producto
        // creado sin stock inicial), se crea en vez de fallar en silencio.
        const { error: invUpdErr } = await supabase
          .from('inventory')
          .upsert({ variant_id: it.variantId, available: next }, { onConflict: 'variant_id' });
        if (invUpdErr) console.warn('[purchases] inventory upsert failed:', invUpdErr.message);

        await supabase.from('inventory_movements').insert({
          variant_id: it.variantId,
          movement_type: 'PURCHASE',
          quantity: it.quantity,
          prev_available: prev,
          new_available: next,
          prev_reserved: reserved,
          new_reserved: reserved,
          prev_consigned: consigned,
          new_consigned: consigned,
          unit_cost: it.unitCost,
          reason: `Compra - ${purchase.invoice_number ?? ''}`,
        });
      }
    }

    return {
      data: {
        id: purchase.id,
        invoiceNumber: purchase.invoice_number ?? '',
        supplierName: '',
        totalCost: Number(purchase.total_cost),
        itemCount: input.items.reduce((sum, it) => sum + it.quantity, 0),
        notes: purchase.notes ?? '',
        createdAt: purchase.created_at ?? '',
      },
      error: null,
    };
  } catch (err) {
    console.warn('[purchases] save failed:', err);
    return { data: null, error: 'Error de conexión al guardar la compra.' };
  }
}

// --- Detalle de compra (qué se compró) ---

export interface PurchaseItemDetail {
  id: string;
  productName: string;
  sizeLabel: string;
  quantity: number;
  unitCost: number;
  retailPrice: number;
}

export interface PurchasePaymentDetail {
  id: string;
  amount: number;
  paidAt: string;
  notes: string;
  createdAt: string;
}

export interface PurchaseDetail {
  invoiceNumber: string;
  supplierName: string;
  totalCost: number;
  notes: string;
  createdAt: string;
  status: PurchaseStatus;
  paidAmount: number;
  balance: number;
  receiptUrl: string;
  items: PurchaseItemDetail[];
  payments: PurchasePaymentDetail[];
}

export async function fetchPurchaseDetail(id: string): Promise<PurchaseDetail | null> {
  if (!isSupabaseConfigured || !supabase) return null;

  const [purRes, itemsRes, suppliersRes, variantsRes, productsRes, sizesRes, paymentsRes] =
    await Promise.all([
      supabase.from('purchases').select('*').eq('id', id).maybeSingle(),
      supabase.from('purchase_items').select('*').eq('purchase_id', id),
      supabase.from('suppliers').select('id, company_name'),
      supabase.from('product_variants').select('*'),
      supabase.from('products').select('id, name'),
      supabase.from('sizes').select('*'),
      supabase.from('supplier_payments').select('*').eq('purchase_id', id),
    ]);

  if (!purRes.data) return null;

  const pur = purRes.data as Record<string, any>;
  const supplierMap = new Map(
    ((suppliersRes.data ?? []) as Array<Record<string, any>>).map((s) => [s.id, s.company_name]),
  );
  const variantMap = new Map(
    ((variantsRes.data ?? []) as Array<Record<string, any>>).map((v) => [v.id, v]),
  );
  const productMap = new Map(
    ((productsRes.data ?? []) as Array<Record<string, any>>).map((p) => [p.id, p.name]),
  );
  const sizeMap = new Map(
    ((sizesRes.data ?? []) as Array<Record<string, any>>).map((s) => [s.id, s.name]),
  );

  const items: PurchaseItemDetail[] = ((itemsRes.data ?? []) as Array<Record<string, any>>).map(
    (it) => {
      const variant = variantMap.get(it.variant_id);
      return {
        id: it.id,
        productName: variant ? productMap.get(variant.product_id) ?? '' : '',
        sizeLabel: variant ? sizeMap.get(variant.size_id) ?? '' : '',
        quantity: it.quantity,
        unitCost: Number(it.unit_cost),
        retailPrice: Number(it.retail_price ?? 0),
      };
    },
  );

  const payments: PurchasePaymentDetail[] = ((paymentsRes.data ?? []) as Array<Record<string, any>>)
    .map((p) => ({
      id: p.id,
      amount: Number(p.amount),
      paidAt: p.paid_at ?? '',
      notes: p.notes ?? '',
      createdAt: p.created_at ?? '',
    }))
    .sort((a, b) => (a.paidAt < b.paidAt ? 1 : -1));

  const paidAmount = {
    total: Number(pur.total_cost),
    paid: payments.reduce((sum, p) => sum + p.amount, 0),
  };

  return {
    invoiceNumber: pur.invoice_number ?? '',
    supplierName: pur.supplier_id ? supplierMap.get(pur.supplier_id) ?? '' : '',
    totalCost: Number(pur.total_cost),
    notes: pur.notes ?? '',
    createdAt: pur.created_at ?? '',
    status: computePurchaseStatus(paidAmount.total, paidAmount.paid, (pur.status ?? 'pending') as string),
    paidAmount: paidAmount.paid,
    balance: Math.max(0, paidAmount.total - paidAmount.paid),
    receiptUrl: pur.receipt_url ?? '',
    items,
    payments,
  };
}

// Elimina una compra (factura) de forma permanente. Como las compras suman
// stock al inventario, al eliminarlas se revierte ese ingreso: se descuenta la
// cantidad de cada insumo y se registra un movimiento de cancelación. Sirve
// para corregir equivocaciones o facturaciones duplicadas.
export async function deletePurchase(id: string): Promise<{ error: string | null }> {
  if (!(await canDelete('purchases'))) {
    return { error: DELETE_PERMISSION_DENIED };
  }
  if (!isSupabaseConfigured || !supabase) {
    return { error: 'Supabase no está configurado.' };
  }

  try {
    const { data: pur } = await supabase
      .from('purchases')
      .select('invoice_number')
      .eq('id', id)
      .maybeSingle();

    const { data: items } = await supabase.from('purchase_items').select('*').eq('purchase_id', id);

    // Revierte el ingreso de stock de cada insumo de la compra.
    for (const it of (items ?? []) as Array<Record<string, any>>) {
      const { data: inv } = await supabase
        .from('inventory')
        .select('available, reserved, consigned')
        .eq('variant_id', it.variant_id)
        .maybeSingle();

      const prev = Number(inv?.available ?? 0);
      const reserved = Number(inv?.reserved ?? 0);
      const consigned = Number(inv?.consigned ?? 0);
      const next = Math.max(0, prev - Number(it.quantity));

      await supabase.from('inventory').update({ available: next }).eq('variant_id', it.variant_id);

      await supabase.from('inventory_movements').insert({
        variant_id: it.variant_id,
        movement_type: 'PURCHASE_CANCEL',
        quantity: -Number(it.quantity),
        prev_available: prev,
        new_available: next,
        prev_reserved: reserved,
        new_reserved: reserved,
        prev_consigned: consigned,
        new_consigned: consigned,
        unit_cost: Number(it.unit_cost),
        reason: `Eliminación de compra - ${(pur as Record<string, any> | null)?.invoice_number ?? ''}`,
      });
    }

    // Elimina los items y luego la compra (en este orden por la FK).
    await supabase.from('purchase_items').delete().eq('purchase_id', id);
    const { error } = await supabase.from('purchases').delete().eq('id', id);
    if (error) return { error: error.message };

    recordAudit({ entityType: 'purchases', entityId: id, action: 'DELETE' });
    return { error: null };
  } catch (err) {
    console.warn('[purchases] delete failed:', err);
    return { error: 'Error de conexión al eliminar la compra.' };
  }
}

// --- Edición de compra (factura, proveedor, notas) ---

export interface PurchaseUpdateInput {
  supplierId: string;
  invoiceNumber: string;
  notes: string;
}

// Actualiza los datos editables de una compra ya registrada (por ejemplo
// asignar el número de factura cuando llegó "sin factura").
export async function updatePurchase(
  id: string,
  input: PurchaseUpdateInput,
): Promise<{ error: string | null }> {
  if (!(await canWrite('purchases'))) return { error: WRITE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) return { error: 'Supabase no está configurado.' };

  try {
    const { error } = await supabase
      .from('purchases')
      .update({
        supplier_id: input.supplierId || null,
        invoice_number: input.invoiceNumber.trim() || null,
        notes: input.notes.trim() || null,
      })
      .eq('id', id);
    if (error) return { error: error.message };

    recordAudit({
      entityType: 'purchases',
      entityId: id,
      action: 'UPDATE',
      fieldName: 'invoice_number',
      newValue: input.invoiceNumber.trim(),
    });
    return { error: null };
  } catch (err) {
    console.warn('[purchases] update failed:', err);
    return { error: 'Error de conexión al actualizar la compra.' };
  }
}

// --- Abonos (pagos parciales) de una compra ---

export interface PurchasePaymentInput {
  purchaseId: string;
  amount: number;
  paidAt: string;
  notes: string;
}

// Recalcula el estado de pago de una compra sumando sus abonos.
async function refreshPurchaseStatus(purchaseId: string): Promise<void> {
  if (!supabase) return;
  const { data: pur } = await supabase
    .from('purchases')
    .select('total_cost, status')
    .eq('id', purchaseId)
    .maybeSingle();
  if (!pur) return;

  const { data: pays } = await supabase
    .from('supplier_payments')
    .select('amount')
    .eq('purchase_id', purchaseId);
  const paid = ((pays ?? []) as Array<Record<string, any>>).reduce(
    (sum, p) => sum + Number(p.amount),
    0,
  );
  const status = computePurchaseStatus(
    Number(pur.total_cost),
    paid,
    (pur.status ?? 'pending') as string,
  );
  await supabase.from('purchases').update({ status }).eq('id', purchaseId);
}

// Registra un abono a una compra y recalcula su estado (pendiente/abonada/pagada).
export async function recordPurchasePayment(
  input: PurchasePaymentInput,
): Promise<{ error: string | null }> {
  if (!(await canWrite('purchases'))) return { error: WRITE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) return { error: 'Supabase no está configurado.' };
  if (!input.amount || input.amount <= 0) return { error: 'Ingresa un monto válido para el abono.' };

  try {
    const { data: pur } = await supabase
      .from('purchases')
      .select('supplier_id')
      .eq('id', input.purchaseId)
      .maybeSingle();
    const supplierId = (pur as Record<string, any> | null)?.supplier_id ?? null;

    const { error } = await supabase.from('supplier_payments').insert({
      supplier_id: supplierId,
      purchase_id: input.purchaseId,
      amount: Number(input.amount),
      paid_at: input.paidAt || new Date().toISOString(),
      notes: input.notes.trim() || null,
    });
    if (error) return { error: error.message };

    await refreshPurchaseStatus(input.purchaseId);
    recordAudit({
      entityType: 'purchases',
      entityId: input.purchaseId,
      action: 'UPDATE',
      fieldName: 'abono',
      newValue: `$${Number(input.amount)}`,
    });
    return { error: null };
  } catch (err) {
    console.warn('[purchases] payment failed:', err);
    return { error: 'Error de conexión al registrar el abono.' };
  }
}

// Elimina un abono (corrección) y recalcula el estado de la compra.
export async function deletePurchasePayment(
  paymentId: string,
  purchaseId: string,
): Promise<{ error: string | null }> {
  if (!(await canWrite('purchases'))) return { error: WRITE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) return { error: 'Supabase no está configurado.' };

  try {
    const { error } = await supabase.from('supplier_payments').delete().eq('id', paymentId);
    if (error) return { error: error.message };
    await refreshPurchaseStatus(purchaseId);
    return { error: null };
  } catch (err) {
    console.warn('[purchases] delete payment failed:', err);
    return { error: 'Error de conexión al eliminar el abono.' };
  }
}

// --- Comprobante de la compra (imagen opcional) ---

// Sube una imagen de comprobante al bucket público compartido y devuelve su URL.
export async function uploadPurchaseReceipt(
  file: File,
): Promise<{ url: string | null; error: string | null }> {
  if (!isSupabaseConfigured || !supabase) {
    return { url: null, error: 'Supabase no está configurado. Conéctalo para subir archivos.' };
  }
  const safeBase =
    file.name
      .replace(/\.[^.]+$/, '')
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40) || 'comprobante';
  const ext = file.name.split('.').pop()?.toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
  const path = `compras/${Date.now()}-${safeBase}.${ext}`;

  try {
    const { error } = await supabase.storage
      .from('product-images')
      .upload(path, file, { cacheControl: '3600', upsert: false, contentType: file.type });
    if (error) return { url: null, error: error.message };

    const { data } = supabase.storage.from('product-images').getPublicUrl(path);
    return { url: data.publicUrl, error: null };
  } catch (err) {
    console.warn('[purchases] receipt upload failed:', err);
    return { url: null, error: 'Error de conexión al subir el comprobante.' };
  }
}

// Guarda (o quita) la URL del comprobante en la compra.
export async function updatePurchaseReceipt(
  id: string,
  url: string,
): Promise<{ error: string | null }> {
  if (!(await canWrite('purchases'))) return { error: WRITE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) return { error: 'Supabase no está configurado.' };

  try {
    const { error } = await supabase
      .from('purchases')
      .update({ receipt_url: url.trim() || null })
      .eq('id', id);
    if (error) return { error: error.message };
    return { error: null };
  } catch (err) {
    console.warn('[purchases] receipt save failed:', err);
    return { error: 'Error de conexión al guardar el comprobante.' };
  }
}

// --- Cancelar compra (revierte el stock que había sumado) ---

// Marca una compra como cancelada y revierte el stock que había ingresado al
// inventario. La compra NO se borra: queda en el historial como cancelada.
export async function cancelPurchase(id: string): Promise<{ error: string | null }> {
  if (!(await canWrite('purchases'))) return { error: WRITE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) return { error: 'Supabase no está configurado.' };

  try {
    const { data: pur } = await supabase
      .from('purchases')
      .select('invoice_number, status')
      .eq('id', id)
      .maybeSingle();
    if (!pur) return { error: 'Compra no encontrada.' };
    if ((pur as Record<string, any>).status === 'cancelled') return { error: null };

    const { data: items } = await supabase.from('purchase_items').select('*').eq('purchase_id', id);

    for (const it of (items ?? []) as Array<Record<string, any>>) {
      const { data: inv } = await supabase
        .from('inventory')
        .select('available, reserved, consigned')
        .eq('variant_id', it.variant_id)
        .maybeSingle();
      const prev = Number(inv?.available ?? 0);
      const reserved = Number(inv?.reserved ?? 0);
      const consigned = Number(inv?.consigned ?? 0);
      const next = Math.max(0, prev - Number(it.quantity));

      await supabase.from('inventory').update({ available: next }).eq('variant_id', it.variant_id);
      await supabase.from('inventory_movements').insert({
        variant_id: it.variant_id,
        movement_type: 'PURCHASE_CANCEL',
        quantity: -Number(it.quantity),
        prev_available: prev,
        new_available: next,
        prev_reserved: reserved,
        new_reserved: reserved,
        prev_consigned: consigned,
        new_consigned: consigned,
        unit_cost: Number(it.unit_cost),
        reason: `Cancelación de compra - ${(pur as Record<string, any>).invoice_number ?? ''}`,
      });
    }

    const { error } = await supabase.from('purchases').update({ status: 'cancelled' }).eq('id', id);
    if (error) return { error: error.message };

    recordAudit({
      entityType: 'purchases',
      entityId: id,
      action: 'UPDATE',
      fieldName: 'status',
      newValue: 'cancelled',
    });
    return { error: null };
  } catch (err) {
    console.warn('[purchases] cancel failed:', err);
    return { error: 'Error de conexión al cancelar la compra.' };
  }
}